import type { BillingBudget, BillingBudgetField } from '../types/billing';
import { BILLING_NUMERIC_FIELDS } from '../types/billing';
import {
  BILLING_FILTERABLE_COLUMNS,
  BILLING_SEARCH_FIELDS,
  getBillingColumnLabel,
} from './billingTableConfig';
import { formatBillingNumber } from './billingHelpers';

export interface BillingColumnFilterMenuState {
  anchorEl: HTMLElement | null;
  selectedValues: string[];
}

export type BillingQuickFilter = 'flagged' | 'over_budget' | 'missing_currency';

export function getBillingCellFilterValue(row: BillingBudget, columnId: BillingBudgetField): string {
  const value = row[columnId];
  if (columnId === 'is_flagged') {
    return value ? 'Yes' : 'No';
  }
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
}

export function getBillingCellDisplayText(
  row: BillingBudget,
  columnId: BillingBudgetField,
): string {
  if (columnId === 'is_flagged') {
    return row.is_flagged ? 'Yes' : 'No';
  }
  if (BILLING_NUMERIC_FIELDS.includes(columnId)) {
    return formatBillingNumber(row[columnId] as number | null);
  }
  const value = row[columnId];
  return value != null && value !== '' ? String(value) : '';
}

export function applyBillingSearch(rows: BillingBudget[], searchTerm: string): BillingBudget[] {
  const q = searchTerm.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    BILLING_SEARCH_FIELDS.some((field) => {
      const v = row[field];
      if (v === null || v === undefined) return false;
      return String(v).toLowerCase().includes(q);
    }),
  );
}

export function applyBillingColumnFilters(
  rows: BillingBudget[],
  filterMenus: Record<string, BillingColumnFilterMenuState>,
  excludeColumnId?: string,
): BillingBudget[] {
  let result = rows;
  for (const columnId of BILLING_FILTERABLE_COLUMNS) {
    if (excludeColumnId && columnId === excludeColumnId) continue;
    const selected = filterMenus[columnId]?.selectedValues ?? [];
    if (selected.length === 0) continue;
    result = result.filter((row) => {
      const v = getBillingCellFilterValue(row, columnId);
      return selected.includes(v);
    });
  }
  return result;
}

export function applyBillingQuickFilters(
  rows: BillingBudget[],
  active: Set<BillingQuickFilter>,
): BillingBudget[] {
  if (active.size === 0) return rows;
  return rows.filter((row) => {
    if (active.has('flagged') && !row.is_flagged) return false;
    if (active.has('missing_currency') && !(row.currency ?? '').trim()) return false;
    if (active.has('over_budget')) {
      const ytdPct = row.ytd_percentage;
      const overByPct = ytdPct != null && !Number.isNaN(ytdPct) && ytdPct > 100;
      const statusOver =
        (row.status ?? '').toLowerCase().includes('over') ||
        (row.status ?? '').toLowerCase().includes('exceed');
      if (!overByPct && !statusOver) return false;
    }
    return true;
  });
}

export function compareBillingRows(
  a: BillingBudget,
  b: BillingBudget,
  columnId: BillingBudgetField,
): number {
  const av = a[columnId];
  const bv = b[columnId];

  if (columnId === 'is_flagged') {
    return Number(a.is_flagged) - Number(b.is_flagged);
  }

  if (BILLING_NUMERIC_FIELDS.includes(columnId)) {
    const an = av === null || av === undefined || Number.isNaN(Number(av)) ? null : Number(av);
    const bn = bv === null || bv === undefined || Number.isNaN(Number(bv)) ? null : Number(bv);
    if (an === null && bn === null) return 0;
    if (an === null) return 1;
    if (bn === null) return -1;
    return an - bn;
  }

  const as = av == null ? '' : String(av).toLowerCase();
  const bs = bv == null ? '' : String(bv).toLowerCase();
  return as.localeCompare(bs);
}

export function sortBillingRows(
  rows: BillingBudget[],
  orderBy: BillingBudgetField | null,
  order: 'asc' | 'desc',
): BillingBudget[] {
  if (!orderBy) return rows;
  const sorted = [...rows].sort((a, b) => compareBillingRows(a, b, orderBy));
  return order === 'desc' ? sorted.reverse() : sorted;
}

export function getUniqueBillingFilterValues(
  rows: BillingBudget[],
  columnId: BillingBudgetField,
): string[] {
  const set = new Set<string>();
  rows.forEach((row) => {
    const v = getBillingCellFilterValue(row, columnId);
    if (v) set.add(v);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function hasActiveBillingFilters(
  search: string,
  filterMenus: Record<string, BillingColumnFilterMenuState>,
  quickFilters: Set<BillingQuickFilter>,
): boolean {
  if (search.trim()) return true;
  if (quickFilters.size > 0) return true;
  return BILLING_FILTERABLE_COLUMNS.some(
    (id) => (filterMenus[id]?.selectedValues?.length ?? 0) > 0,
  );
}

export function billingExportHeaders(
  visibleColumnIds: BillingBudgetField[],
): { id: BillingBudgetField; label: string }[] {
  return visibleColumnIds.map((id) => ({
    id,
    label: getBillingColumnLabel(id),
  }));
}
